import { useEffect, useState } from 'react';
import { format, isPast } from 'date-fns';
import { Calendar, Clock, ArrowLeftRight, X, Check, XCircle } from 'lucide-react';
import { useShiftStore, useUserStore, useToastStore, useAuthStore } from '../store';
import { Spinner, EmptyState, Badge, Modal } from '../components/ui';
import { api } from '../utils/api';
import { SHIFT_TIME_SLOTS, type Shift, type ShiftSwap, type ShiftDropRequest } from '../types';

export function MyShiftsPage() {
  const { myShifts, isLoading, error, fetchMyShifts } = useShiftStore();
  const { users, fetchUsers } = useUserStore();
  const { addToast } = useToastStore();
  const { user: currentUser } = useAuthStore();
  
  const [pendingSwaps, setPendingSwaps] = useState<ShiftSwap[]>([]);
  const [myDropRequests, setMyDropRequests] = useState<ShiftDropRequest[]>([]);
  
  // Modal states
  const [showSwapModal, setShowSwapModal] = useState(false);
  const [showDropModal, setShowDropModal] = useState(false);
  const [selectedShift, setSelectedShift] = useState<Shift | null>(null);
  const [selectedTargetUser, setSelectedTargetUser] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [processingSwapId, setProcessingSwapId] = useState<string | null>(null);

  useEffect(() => {
    fetchMyShifts();
    fetchUsers();
    loadPendingSwaps();
    loadMyDropRequests();
  }, [fetchMyShifts, fetchUsers]);

  const loadPendingSwaps = async () => {
    try {
      const result = await api.getMySwaps();
      if (result.success && result.data) {
        setPendingSwaps(result.data);
      }
    } catch (error) {
      console.error('Failed to load swaps:', error);
    }
  };

  const loadMyDropRequests = async () => {
    try {
      const result = await api.getMyDropRequests();
      if (result.success && result.data) {
        setMyDropRequests(result.data);
      }
    } catch (error) {
      console.error('Failed to load drop requests:', error);
    }
  };

  const getTimeLabel = (startTime: string) => {
    return SHIFT_TIME_SLOTS.find(s => s.start === startTime)?.label || startTime;
  };

  const handleSwapClick = (shift: Shift) => {
    setSelectedShift(shift);
    setSelectedTargetUser('');
    setShowSwapModal(true);
  };

  const handleDropClick = (shift: Shift) => {
    setSelectedShift(shift);
    setShowDropModal(true);
  };

  const handleSwapSubmit = async () => {
    if (!selectedShift || !selectedTargetUser) {
      addToast('Please select a user to swap with', 'warning');
      return;
    }

    if (isSubmitting) return;
    
    setIsSubmitting(true);
    try {
      console.log('Creating swap request:', { shiftId: selectedShift.id, targetUserId: selectedTargetUser });
      const result = await api.createSwapRequest(selectedShift.id, selectedTargetUser);
      console.log('Swap request result:', result);
      
      if (result.success) {
        addToast('Swap request sent!', 'success');
        // Close modal immediately
        setShowSwapModal(false);
        setSelectedShift(null);
        setSelectedTargetUser('');
        
        // Refresh data in background
        Promise.all([loadPendingSwaps(), fetchMyShifts()]).catch(err => {
          console.error('Error refreshing data:', err);
        });
      } else {
        addToast(result.error || 'Failed to send swap request', 'error');
      }
    } catch (error) {
      console.error('Swap request error:', error);
      addToast('Failed to send swap request', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDropSubmit = async () => {
    if (!selectedShift) return;

    if (isSubmitting) return;
    
    setIsSubmitting(true);
    try {
      console.log('Creating drop request for shift:', selectedShift.id);
      const result = await api.createDropRequest(selectedShift.id);
      console.log('Drop request result:', result);
      
      if (result.success) {
        addToast('Drop request submitted!', 'success');
        // Close modal immediately
        setShowDropModal(false);
        setSelectedShift(null);
        
        // Refresh data in background
        Promise.all([loadMyDropRequests(), fetchMyShifts()]).catch(err => {
          console.error('Error refreshing data:', err);
        });
      } else {
        addToast(result.error || 'Failed to submit drop request', 'error');
      }
    } catch (error) {
      console.error('Drop request error:', error);
      addToast('Failed to submit drop request', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAcceptSwap = async (swapId: string) => {
    if (processingSwapId) return;
    
    setProcessingSwapId(swapId);
    try {
      const result = await api.acceptSwap(swapId);
      if (result.success) {
        addToast('Swap accepted!', 'success');
        // Refresh data
        await Promise.all([loadPendingSwaps(), fetchMyShifts()]);
      } else {
        addToast(result.error || 'Failed to accept swap', 'error');
      }
    } catch (error) {
      console.error('Accept swap error:', error);
      addToast('Failed to accept swap', 'error');
    } finally {
      setProcessingSwapId(null);
    }
  };

  const handleRejectSwap = async (swapId: string) => {
    if (processingSwapId) return;
    
    setProcessingSwapId(swapId);
    try {
      const result = await api.rejectSwap(swapId);
      if (result.success) {
        addToast('Swap rejected', 'success');
        // Refresh data
        await loadPendingSwaps();
      } else {
        addToast(result.error || 'Failed to reject swap', 'error');
      }
    } catch (error) {
      console.error('Reject swap error:', error);
      addToast('Failed to reject swap', 'error');
    } finally {
      setProcessingSwapId(null);
    }
  };

  // Filter out shifts that have pending drop requests
  const safeDropRequests = (myDropRequests && Array.isArray(myDropRequests)) ? myDropRequests : [];
  const shiftsWithDropRequests = new Set(safeDropRequests.filter(r => r.status === 'Pending').map(r => r.shift_id));
  const safeMyShifts = (myShifts && Array.isArray(myShifts)) ? myShifts : [];
  const displayableShifts = safeMyShifts.filter(shift => !shiftsWithDropRequests.has(shift.id));

  // Get swaps where user is the target (pending acceptance)
  const safePendingSwaps = (pendingSwaps && Array.isArray(pendingSwaps)) ? pendingSwaps : [];
  const swapsToRespond = safePendingSwaps.filter(swap => 
    swap.status === 'Pending' && currentUser && swap.target_user_id === currentUser.id
  );

  // Get swaps where user is the requester
  const mySwapRequests = safePendingSwaps.filter(swap => 
    swap.status === 'Pending' && currentUser && swap.requesting_user_id === currentUser.id
  );

  if (isLoading && (!myShifts || !Array.isArray(myShifts) || myShifts.length === 0)) {
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900">My Shifts</h1>
        <p className="text-slate-500 mt-1">Your assigned shifts and pending swap requests</p>
      </div>

      {error && (
        <div className="card bg-red-50 border-red-200 p-4 mb-4">
          <p className="text-red-800">Error loading shifts: {error}</p>
        </div>
      )}

      {/* Pending Swaps to Respond To */}
      {swapsToRespond.length > 0 && (
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-3">Pending Swap Requests</h2>
          <div className="grid gap-4">
            {swapsToRespond.map(swap => (
              <div key={swap.id} className="card p-6 border-2 border-amber-200 bg-amber-50">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                      <ArrowLeftRight className="w-6 h-6 text-amber-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">
                        Swap Request from {swap.requesting_user?.display_name || swap.requesting_user?.username}
                      </h3>
                      {swap.shift && (
                        <>
                          <p className="text-slate-600 mt-1">
                            {format(new Date(swap.shift.date + 'T00:00:00'), 'EEEE, MMMM d, yyyy')}
                          </p>
                          <p className="text-slate-600">{getTimeLabel(swap.shift.start_time)}</p>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleRejectSwap(swap.id)}
                      disabled={processingSwapId !== null}
                      className="btn btn-danger btn-sm"
                    >
                      {processingSwapId === swap.id ? (
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      ) : (
                        <>
                          <XCircle className="w-4 h-4" />
                          Reject
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => handleAcceptSwap(swap.id)}
                      disabled={processingSwapId !== null}
                      className="btn btn-success btn-sm"
                    >
                      {processingSwapId === swap.id ? (
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      ) : (
                        <>
                          <Check className="w-4 h-4" />
                          Accept
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* My Shifts */}
      {displayableShifts.length === 0 && !isLoading ? (
        <div className="card">
          <EmptyState
            icon={<Calendar className="w-12 h-12 mx-auto" />}
            title="No assigned shifts"
            description="You don't have any shifts assigned yet. Check the schedule to request shifts."
          />
        </div>
      ) : displayableShifts.length > 0 ? (
        <div className="grid gap-4">
          {displayableShifts.map(shift => {
            const shiftDate = new Date(shift.date + 'T00:00:00');
            const isPastShift = isPast(shiftDate);
            const hasPendingSwap = mySwapRequests.some(s => s.shift_id === shift.id);
            
            return (
              <div key={shift.id} className="card p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center">
                      <Clock className="w-6 h-6 text-primary-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">
                        {format(shiftDate, 'EEEE, MMMM d, yyyy')}
                      </h3>
                      <p className="text-slate-600 mt-1">{getTimeLabel(shift.start_time)}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <Badge
                          variant={
                            shift.assignment_type === 'Permanent' ? 'info' :
                            shift.assignment_type === 'Admin' ? 'purple' : 'success'
                          }
                        >
                          {shift.assignment_type}
                        </Badge>
                        {hasPendingSwap && (
                          <Badge variant="warning">Swap Pending</Badge>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {isPastShift ? (
                      <Badge variant="default">Past</Badge>
                    ) : (
                      <>
                        <Badge variant="success">Assigned</Badge>
                        <button
                          onClick={() => handleSwapClick(shift)}
                          className="btn btn-secondary btn-sm"
                          title="Request swap"
                        >
                          <ArrowLeftRight className="w-4 h-4" />
                          Swap
                        </button>
                        <button
                          onClick={() => handleDropClick(shift)}
                          className="btn btn-danger btn-sm"
                          title="Request to drop shift"
                        >
                          <X className="w-4 h-4" />
                          Drop
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : null}

      {/* Swap Modal */}
      <Modal
        isOpen={showSwapModal}
        onClose={() => {
          if (!isSubmitting) {
            setShowSwapModal(false);
            setSelectedShift(null);
            setSelectedTargetUser('');
          }
        }}
        title="Request Shift Swap"
      >
        {selectedShift && (
          <div className="space-y-4">
            <div className="p-4 bg-slate-50 rounded-xl">
              <p className="font-medium text-slate-900">
                {format(new Date(selectedShift.date + 'T00:00:00'), 'EEEE, MMMM d, yyyy')}
              </p>
              <p className="text-slate-600">
                {getTimeLabel(selectedShift.start_time)}
              </p>
            </div>
            <div>
              <label className="input-label">Swap with User</label>
              <select
                value={selectedTargetUser}
                onChange={(e) => setSelectedTargetUser(e.target.value)}
                className="input"
              >
                <option value="">Select a user...</option>
                {(users && Array.isArray(users) ? users : [])
                  .filter(u => u.status === 'Active' && u.id !== selectedShift.assigned_users?.[0]?.id)
                  .map(u => (
                    <option key={u.id} value={u.id}>
                      {u.display_name || u.username} ({u.email})
                    </option>
                  ))}
              </select>
            </div>
            <p className="text-sm text-slate-500">
              The selected user will receive an email notification and can accept or reject your swap request.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  if (!isSubmitting) {
                    setShowSwapModal(false);
                    setSelectedShift(null);
                    setSelectedTargetUser('');
                  }
                }}
                disabled={isSubmitting}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleSwapSubmit}
                disabled={isSubmitting || !selectedTargetUser}
                className="btn btn-primary"
              >
                {isSubmitting ? (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  'Send Swap Request'
                )}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Drop Modal */}
      <Modal
        isOpen={showDropModal}
        onClose={() => {
          if (!isSubmitting) {
            setShowDropModal(false);
            setSelectedShift(null);
          }
        }}
        title="Request to Drop Shift"
      >
        {selectedShift && (
          <div className="space-y-4">
            <div className="p-4 bg-slate-50 rounded-xl">
              <p className="font-medium text-slate-900">
                {format(new Date(selectedShift.date + 'T00:00:00'), 'EEEE, MMMM d, yyyy')}
              </p>
              <p className="text-slate-600">
                {getTimeLabel(selectedShift.start_time)}
              </p>
            </div>
            <p className="text-slate-600">
              Are you sure you want to request to drop this shift? An admin will review your request and approve or reject it.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  if (!isSubmitting) {
                    setShowDropModal(false);
                    setSelectedShift(null);
                  }
                }}
                disabled={isSubmitting}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleDropSubmit}
                disabled={isSubmitting}
                className="btn btn-danger"
              >
                {isSubmitting ? (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  'Request to Drop'
                )}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
